import React, { useRef, useCallback } from 'react';
import styled from 'styled-components/macro';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faAngleDoubleRight, IconDefinition, faAngleDoubleUp } from '@fortawesome/free-solid-svg-icons';
import { toggleTopicContent } from 'redux/actions/topicAction';
import { useDispatch } from 'react-redux';
import { useViewport } from 'hooks/useViewport';
import { theme } from 'theme';
import { msTransition } from 'helpers/msTransition';
import { ITopic } from 'config/topics';

interface ISidebarActionShared {
  id: number;
  icon: IconDefinition;
}

interface ISidebarActionAnchor extends ISidebarActionShared {
  tag: 'a';
  href: string;
}

interface ISidebarActionButton extends ISidebarActionShared {
  tag: 'button';
  onClick: (e: React.MouseEvent) => void;
}

export type SidebarActions = Array<ISidebarActionAnchor | ISidebarActionButton>

type SidebarPosition = { $position: IProps['position'] };

interface IProps {
  show: boolean;
  showToggle: boolean;
  actions: SidebarActions;
  position: ITopic['sidebarPosition'];
  footer?: React.ReactElement | React.ReactText;
}

export const sidebarAnimationDuration = theme.transition.slow;

// Todo: Animate in and out for new item
const StyledSidebar = styled.aside<{ animationState: TransitionStatus } & SidebarPosition>`
  background: ${({ theme }) => theme.colors.white};
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  transition: ${({ animationState }) => {
    switch (animationState) {
      case 'entering':
        return 'none';
      case 'entered':
        return 'none';
      case 'exiting':
        return `transform ${msTransition(sidebarAnimationDuration)}`;
      case 'exited':
        return 'none';
      default:
        break;
    };
  }};
  z-index: 20;
  display: flex;
  flex-direction: column;
  transform: ${({ animationState }) => {
    switch (animationState) {
      case 'entering':
        return 'translate(0%, 0%)';
      case 'entered':
        return 'translate(0%, 0%)';
      case 'exiting':
        return 'translate(0%, 100%)';
      case 'exited':
        return 'translate(0%, 100%)';
      default:
        break;
    };
  }};

  @media (min-width: ${({ theme, }) => `${theme.breakpoints.lg}px`}) { 
    left: auto;
    right: auto;
    ${({ $position }) => `${$position}: 0`};
    max-width: 560px;
    border-radius: ${({ $position }) => {
    switch ($position) {
      case 'left':
        return `0 ${theme.radius.sm} ${theme.radius.sm} 0`;
      case 'right':
        return `${theme.radius.sm} 0 0 ${theme.radius.sm}`;
      default:
        break;
    }
  }};
    box-shadow: ${({ theme }) => theme.elevation.md};
    margin: ${({ theme }) => theme.spacing[8]} 0;
    transform: ${({ animationState, $position }) => {
    switch (animationState) {
      case 'entering':
        return 'translate(0%, 0%)';
      case 'entered':
        return 'translate(0%, 0%)';
      case 'exiting':
        return `translate(${$position === 'right' ? '100%' : '-100%'}, 0%)`;
      case 'exited':
        return `translate(${$position === 'right' ? '100%' : '-100%'}, 0%)`;
      default:
        return '';
    };
  }};
  }
`;

const StyledSidebarToggle = styled.button<SidebarPosition>`
  position: absolute;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.elevation.md};
  padding: ${({ theme }) => theme.spacing['2']};
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => msTransition(theme.transition.normal)};
  right: ${({ theme }) => theme.spacing['3']};
  top: 0;
  transform: translate(0%, -100%);
  border-radius: ${({ theme }) => `${theme.radius.sm} ${theme.radius.sm}`} 0 0;

  @media (min-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    left: auto;
    right: auto;
    ${({ $position }) => $position === 'right' ? 'left: 0;' : 'right: 0;'};
    border-radius: 0 ${({ theme }) => `${theme.radius.sm} ${theme.radius.sm}`} 0;
    top: ${({ theme }) => theme.spacing[4]};
    transform: ${({ $position }) => $position === 'right' ? 'translate(-100%, 0) scaleX(-1)' : 'translate(100%, 0)'};

    &:hover {
      padding-left: ${({ theme }) => theme.spacing['4']}
    }
  }

`;

const StyledSidebarHeader = styled.header<SidebarPosition>`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.alto};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey};
  ${({ $position }) => $position === 'right' && 'flex-direction: row-reverse;'};

  > * + * {
    ${({ $position }) => `margin-${$position}: ${theme.spacing[2]};`};
  }
`;

const StyledSidebarIcon = styled.button`
  background: none;
  display: inline-block;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey};
  border: 0;
  transition: color ${({ theme }) => msTransition(theme.transition.fast)};

  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const StyledSidebarContent = styled.div`
  padding: ${({ theme }) => theme.spacing['6']};
  overflow: auto;
  max-height: 100%;

  h1,
  h2,
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }
`;

const StyledSidebarFooter = styled.footer`
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Sidebar: React.FC<IProps> = React.memo(({ show, showToggle, children, actions, footer, position }) => {
  const nodeRef = useRef(null);
  const dispatch = useDispatch();
  const { width } = useViewport();

  const toggleSidebar = useCallback(() => dispatch(toggleTopicContent(!show)), [dispatch, show]);

  return (
    <React.Fragment>
      <Transition in={show} nodeRef={nodeRef} timeout={sidebarAnimationDuration}>
        {(state) => (
          <StyledSidebar ref={nodeRef} animationState={state} $position={position}>
            {showToggle && !show && (
              <StyledSidebarToggle onClick={toggleSidebar} $position={position}>
                <FontAwesomeIcon
                  icon={width >= theme.breakpoints.lg ? faAngleDoubleRight : faAngleDoubleUp}
                  size="2x"
                  fill="currentColor"
                />
              </StyledSidebarToggle>
            )}
            <StyledSidebarHeader $position={position}>
              {actions && (
                actions.map((x) => {
                  switch (x.tag) {
                    case 'a':
                      return (
                        <StyledSidebarIcon key={x.id} as={x.tag} href={x.href} target="_blank">
                          <FontAwesomeIcon icon={x.icon} fill="currentColor" size="1x" />
                        </StyledSidebarIcon>
                      );
                    case 'button':
                      return (
                        <StyledSidebarIcon key={x.id} as={x.tag} onClick={x.onClick}>
                          <FontAwesomeIcon icon={x.icon} fill="currentColor" size="1x" />
                        </StyledSidebarIcon>
                      );
                    default:
                      throw new Error('The tag you provided is not valid');
                  }
                })
              )}
              <StyledSidebarIcon onClick={toggleSidebar}>
                <FontAwesomeIcon icon={faTimes} fill="currentColor" size="2x" />
              </StyledSidebarIcon>
            </StyledSidebarHeader>
            <StyledSidebarContent>
              {children}
              {footer && (
                <StyledSidebarFooter>
                  {footer}
                </StyledSidebarFooter>
              )}
            </StyledSidebarContent>
          </StyledSidebar>
        )}
      </Transition>
    </React.Fragment>
  );
});

export { Sidebar };
